// This module is using axios: https://github.com/axios/axios
import axios from 'axios'
import { AAC_FIT_UNREAD_FEATURE } from '@amzn/intro-widgets-api'
import { AAC_FIT_DISMISSED_ALERT_WIDGETS, AAC_FIT_DISMISSED_WHATS_NEW_WIDGETS, WeblabedFeatures } from '../common/constants'
import AACChromeInternalAPI, { Feature } from '@amzn/aac-chrome-internal-api'
import { WidgetStatus } from '../common/props'

export const REQUEST_TIMEOUT = 60000 // default request timeout in ms, 1min
export const fitDismissalUrl = '/ccx/ajax/updateIntroWidgetsStatus'
const header = {
  'Content-type': 'application/json; charset=UTF-8',
}

const request = ({ timeout, withCredentials, ...rest }) => {
  const config = {
    timeout: timeout || REQUEST_TIMEOUT,
    withCredentials: withCredentials ?? true,
    ...rest,
  }
  return axios(config)
}

const getFromLocalStorage = (localStorageName): string[] => JSON.parse(localStorage.getItem(localStorageName) || '[]')

export const findFeatureLaunchInfo = (featureName: WeblabedFeatures): Feature | undefined => {
  const chromeContext = AACChromeInternalAPI.getChromeContext()
  if (!chromeContext.featureLaunchInfo) {
    return undefined
  }
  return chromeContext.featureLaunchInfo.features.find((feature: Feature) => feature.name === featureName)
}

export const requestWithRetry = async (config, n = 2) => {
  if (n < 1) throw new Error('Retry cannot be less than 1')
  try {
    return await request(config)
  } catch (err) {
    if (n === 1) throw err
    return requestWithRetry(config, n - 1)
  }
}

const postRequest = ({ url, timeout, csrfToken, header, ...rest }) => {
  const config = {
    timeout: timeout || REQUEST_TIMEOUT,
    withCredentials: true,
    headers: {
      ['anti-csrftoken-a2z']: csrfToken,
      ...header,
    },
    ...rest,
  }
  return axios.post(url, null, config)
}

export const postRequestWithRetry = async (config, n = 2) => {
  if (n < 1) throw new Error('Retry cannot be less than 1')
  try {
    return await postRequest(config)
  } catch (err) {
    if (n === 1) throw err
    return postRequestWithRetry(config, n - 1)
  }
}

export const identifyUnreadFeatures = (featureLists: string[]) => {
  const dismissedModals = JSON.parse(localStorage.getItem(AAC_FIT_DISMISSED_WHATS_NEW_WIDGETS) || '[]')
  return featureLists.filter((feature) => !dismissedModals.includes(feature))
}

export const clearExpiredModals = (featureLists: string[]) => {
  const dismissedModals = JSON.parse(localStorage.getItem(AAC_FIT_UNREAD_FEATURE) || '[]')
  if (dismissedModals?.length > 20) dismissedModals.filter((dismissedModal) => !featureLists.includes(dismissedModal))
  localStorage.setItem(AAC_FIT_UNREAD_FEATURE, JSON.stringify(dismissedModals))
}

export const addDissmissFeature = (modalId: string) => {
  // the naming is confusing, widgets store in AAC_FIT_UNREAD_FEATURE is actually widgets that has been dismissed
  // this will be fixed after AAC_FIT_DISMISSAL dial-up
  const localStorageLocation = AAC_FIT_DISMISSED_WHATS_NEW_WIDGETS
  const dismissedModals = JSON.parse(localStorage.getItem(localStorageLocation) || '[]')
  if (!dismissedModals.includes(modalId)) {
    postRequestWithRetry({
      params: { widgetId: modalId },
      url: fitDismissalUrl,
      timeout: null,
      header,
      csrfToken: window.__AAC_FIT_CSRF_TOKEN__,
    })
    dismissedModals.push(modalId)
    localStorage.setItem(localStorageLocation, JSON.stringify(dismissedModals))
  }
}

export const dismissWidget = (widgetId: string) => {
  postRequestWithRetry({
    params: { widgetId: widgetId },
    url: fitDismissalUrl,
    timeout: null,
    header,
    csrfToken: window.__AAC_FIT_CSRF_TOKEN__,
  })
}

export const filterWidgetsWithDismissal = (introWidgetMetadata: any[]): any[] => {
  return introWidgetMetadata.filter((widget) => widget.widgetStatus !== WidgetStatus.DISMISSED || widget.type == 'POPOVER')
}

export const anchoredAlertBeingDismissed = (widgetId: string): boolean => getFromLocalStorage(AAC_FIT_DISMISSED_ALERT_WIDGETS).includes(widgetId)

// This noop plugin is required to change hasing algo for styled component
// and is need to prevent classname collisions
// eslint-disable-next-line @typescript-eslint/no-empty-function
export function NoopPlugin() {}
Object.defineProperty(NoopPlugin, 'name', { value: 'aac-ccx-fit' })

// Returns the first parent element which has display attribute set to none
export const getHiddenParentElement = (element: Element): Element | null => {
  for (let el = element; el && el !== document.body; el = el.parentNode as Element) {
    if (getComputedStyle(el).display === 'none') {
      return el
    }
  }
  return null
}

// This function checks whether the FIT widget anchor is visible on the page to avoid rendering in the top left corner
export const checkIsAnchorVisible = (anchorId: string, callbackParentChangedVisibility?: () => void): boolean => {
  let isVisible = false

  // Getting a reference to the anchor element
  const element = document.querySelector(`[data-intro-widget-id='${anchorId}']`)

  if (element) {
    // Searching for the first hidden parent element of the anchor
    const hiddenParentElement = getHiddenParentElement(element)

    if (!hiddenParentElement) {
      // If no hidden parent element is found - anchor is visible on the page
      isVisible = true
    } else {
      // If a hidden parent element is found, we create a MutationObserver on that element to track when it becomes visible
      const observer = new MutationObserver((mutationList: MutationRecord[]) => {
        for (const mutation of mutationList) {
          const currentValue = mutation.target['style'].display

          if (currentValue !== 'none') {
            // Calling the callback function to update the isVisibility state in the FIT widget and get it re-rendered
            if (typeof callbackParentChangedVisibility === 'function') {
              callbackParentChangedVisibility()
            }

            observer.disconnect()
          }
        }
      })

      observer.observe(hiddenParentElement, { attributes: true, attributeFilter: ['style'] })
    }
  }

  return isVisible
}

export const getInternalChromeConfig = () => {
  if (document.querySelector('meta[name="a2z:mons_site_name"]')) {
    return window.AACChrome.InternalAPI.InternalChromeConfigHandler.getInstance().internalChromeConfig
  }

  return AACChromeInternalAPI.getChromeContext()
}

export const getIsMobileView = (): boolean => {
  const internalChromeConfig = getInternalChromeConfig()
  return internalChromeConfig?.deviceType === 'mobile' && internalChromeConfig?.mobileWebSupport.includes('ENABLED') && !internalChromeConfig?.forceDesktopSite
}

export const getFeatureLabels = () => {
  return getInternalChromeConfig()?.featureLaunchInfo?.features?.find((feature) => feature.name === 'FIT_LOAD_DELAY')?.content
}
